import {
  DocumentSource,
  OrderDetail,
  PropertyItemRisk
} from '@insurance/api/requests'
import { getEffectiveDateRange } from '@insurance/utils'
import { usePermissionRestriction } from '@shared/hooks'
import { App, Card, Col, Divider, Form, Row } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CustomerStep } from './CustomerStep'
import { Details } from './Details'
import { ItemsList } from './ItemsList'
import { MainStep } from './MainStep'
import { MediaStep } from './MediaStep'
import { PropertyInsuranceControls } from './PropertyInsuranceControls'
import {
  formProps,
  getPropertyValuesFromStorage,
  orderParentOrChildId,
  removePropertyValuesFromStorage,
  savePropertyValuesToStorage
} from './utils'
import {
  iniMixtContractorValues,
  serializeContractorFields
} from '../PersonsFields'
import { PropertyInsuranceProvider } from './PropertyInsuranceContext'

type Props = {
  order?: OrderDetail
  isEditable?: boolean
}

const {
  stepKey,
  priceKey,
  customerProps,
  contractorProps,
  itemsKey,
  validFromProps,
  periodProps,
  installmentsProps,
  orderKey,
  setBeneficiaryKey,
  effectiveDateProps,
  isOnlineKey
} = formProps()

const createForms = [
  <MainStep key={0} />,
  <ItemsList key={1} />,
  <CustomerStep key={2} />
]

const editableForms = [
  <MainStep key={0} />,
  <ItemsList key={1} />,
  <MediaStep key={2} />,
  <CustomerStep key={3} />
]

const initValues = {
  [stepKey.name]: 0,
  [priceKey.name]: undefined,
  [customerProps.name]: undefined,
  [validFromProps.name]: dayjs(),
  [periodProps.name]: undefined,
  [installmentsProps.name]: undefined,
  [itemsKey.name]: [
    {
      name: undefined,
      location: undefined,
      street: undefined,
      number: undefined,
      block: undefined,
      apartment: undefined,
      country: undefined,
      district: undefined,
      category: undefined,
      risks: undefined,
      cost: undefined,
      amount: undefined,
      coefficients: undefined
    }
  ],
  [orderKey.name]: undefined,
  [effectiveDateProps.name]: undefined,
  ...iniMixtContractorValues
}

export const PropertyInsurance = ({ order, isEditable }: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isReady, setIsReady] = useState(false)
  const { modal } = App.useApp()
  const { hasPermission } = usePermissionRestriction()
  const isEditableMode = !!(isEditable || order?.parent)

  const handleReadyToSaveData = () => setIsReady(true)

  const saveOnChangeValue = () => {
    const values = form.getFieldsValue(true)

    savePropertyValuesToStorage(values, order?.id)

    return null
  }

  const formattedInitValues = useMemo(() => {
    const detailValues = order?.data

    if (!detailValues) return initValues

    const personDateOfBirth = detailValues?.contractor?.person?.date_of_birth

    const formattedItems = detailValues.items?.map((item: AnyObject) => {
      const formattedItem = { ...item }

      if (Array.isArray(item.risks)) {
        formattedItem.risks = item?.risks?.map(
          (riskItem: PropertyItemRisk) => riskItem?.risk
        )
      }

      if (formattedItem.category) {
        formattedItem.category = formattedItem.category.toLowerCase()
      }

      if (Array.isArray(item.coefficients)) {
        formattedItem.coefficients = item?.coefficients?.map((coefficient) => ({
          coefficient: coefficient?.coefficient,
          coefficient_value: coefficient?.value
        }))
      }

      return formattedItem
    })

    const formattedContractor = { ...detailValues.contractor }
    if (personDateOfBirth && formattedContractor.person) {
      formattedContractor.person = {
        ...formattedContractor.person,
        date_of_birth: dayjs(personDateOfBirth)
      }
    }

    const effectiveDateRange = getEffectiveDateRange(order)
    serializeContractorFields({ hasPermission }, detailValues)

    return {
      ...initValues,
      ...detailValues,
      ...(detailValues.valid_from && {
        valid_from: dayjs(detailValues.valid_from)
      }),
      [effectiveDateProps.name]: effectiveDateRange.minDate,
      [stepKey.name]: order?.stage ?? 0,
      [contractorProps.name]: formattedContractor,
      [customerProps.name]: order?.data?.customer || order?.customer,
      [priceKey.name]:
        order?.full_price && order?.currency
          ? { amount: order.full_price, currency: order.currency }
          : undefined,
      [orderKey.name]:
        isEditableMode && orderParentOrChildId(order)
          ? order?.parent?.id || order?.id
          : order?.id,
      ...(formattedItems && {
        [itemsKey.name]: formattedItems
      }),
      [setBeneficiaryKey.name]: !!detailValues.beneficiary,
      [isOnlineKey.name]:
        !isEditableMode && order.source === DocumentSource.WEBSITE
    }
  }, [order, isEditableMode])

  useEffect(() => {
    const storedValues = getPropertyValuesFromStorage(order?.id)
    const values = {
      ...formattedInitValues,
      ...storedValues
    }

    form.setFieldsValue(values)
  }, [form, formattedInitValues, order?.id])

  useEffect(() => {
    const values = getPropertyValuesFromStorage(order?.id)
    if (!values) return

    const confirmModal = modal.confirm({
      onCancel: () => {
        removePropertyValuesFromStorage(order?.id)
        setIsReady(false)
        form.setFieldsValue(formattedInitValues)
      },
      title: t('have_saved_data'),
      onOk: () => setIsReady(true)
    })

    return () => confirmModal.destroy()
  }, [form, t, formattedInitValues, order?.id, modal])

  const forms = useMemo(
    () => (isEditableMode ? editableForms : createForms),
    [isEditableMode]
  )

  return (
    <PropertyInsuranceProvider
      isEditable={isEditableMode}
      {...(isEditableMode && { order })}
    >
      <Form
        layout="vertical"
        form={form}
        onValuesChange={handleReadyToSaveData}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={18}>
            <Form.Item shouldUpdate noStyle>
              {() => (
                <Card>{forms?.[form?.getFieldValue?.(stepKey?.name)]}</Card>
              )}
            </Form.Item>
          </Col>

          <Col xs={24} lg={6}>
            <Card>
              <Details />
            </Card>
          </Col>
        </Row>

        <Divider />

        <PropertyInsuranceControls
          steps={forms.length}
          onReadyToSaveData={handleReadyToSaveData}
        />

        {isReady && (
          <Form.Item shouldUpdate noStyle>
            {saveOnChangeValue}
          </Form.Item>
        )}
      </Form>
    </PropertyInsuranceProvider>
  )
}
